import React, { useMemo } from "react";

import { PAGE_FILTERS, ROW_HOVER, STATUS_META } from "./constants";
import {
  AuditSourceTitle,
  FilterChip,
  GradeDonut,
  PanelHeader,
  ReportSelector,
  RowChevron,
  SideBlock,
  SideStack,
  StatusCounts,
  TwoColumn,
} from "./shared";
import { formatTemplatePath, pageMatches, pageProblemScore, tallyChecks } from "./utils";

export const PagesPanel = ({
  pagesData,
  loading,
  project,
  filter,
  onFilter,
  reports,
  reportFilter,
  onReportFilter,
  onOpenPage,
}) => {
  const pages = pagesData?.pages || [];
  const visible = useMemo(() => {
    const filtered = pages.filter((page) => pageMatches(page, filter));
    return filtered.slice().sort((a, b) => {
      const aChecks = (a.audit || a.page_speed || a.ux_audit)?.checks;
      const bChecks = (b.audit || b.page_speed || b.ux_audit)?.checks;
      return pageProblemScore(bChecks) - pageProblemScore(aChecks);
    });
  }, [pages, filter]);

  const aggregate = useMemo(() => {
    const counts = { pass: 0, warn: 0, fail: 0, skipped: 0 };
    pages.forEach((page) => {
      const audit = page.audit || page.page_speed || page.ux_audit;
      const t = tallyChecks(audit?.checks);
      counts.pass += t.pass;
      counts.warn += t.warn;
      counts.fail += t.fail;
      counts.skipped += t.skipped;
    });
    return counts;
  }, [pages]);

  const hasChecks = aggregate.pass + aggregate.warn + aggregate.fail + aggregate.skipped > 0;
  const side = hasChecks ? (
    <SideStack>
      <SideBlock
        title="Overall checks"
        meta={<StatusCounts counts={aggregate} />}
      >
        <div className="mt-3">
          <GradeDonut
            counts={aggregate}
            center={aggregate.fail + aggregate.warn}
            caption="issues"
          />
        </div>
      </SideBlock>
    </SideStack>
  ) : null;

  return (
    <TwoColumn
      side={side}
      main={(
        <>
          <PanelHeader
            title={(
              <AuditSourceTitle
                noun="Pages"
                project={project}
                reports={reports}
                reportFilter={reportFilter}
                auditHash={pagesData?.audit_report}
              />
            )}
            count={visible.length}
            filters={(
              <>
                {PAGE_FILTERS.map((item) => (
                  <FilterChip
                    key={item.id}
                    active={filter === item.id}
                    onClick={() => onFilter(item.id)}
                  >
                    {item.label}
                  </FilterChip>
                ))}
                <ReportSelector
                  reports={reports}
                  value={reportFilter || ""}
                  onChange={onReportFilter}
                />
                {!pagesData?.actionable && (
                  <span className="text-xs text-amber-500">Read-only</span>
                )}
              </>
            )}
          />
          {loading && !pages.length ? (
            <p className="text-sm text-theme-secondary-text">Loading pages…</p>
          ) : (
            <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
              {visible.map((page) => {
                const audit = page.audit || page.page_speed || page.ux_audit;
                const counts = tallyChecks(audit?.checks);
                const displayPath = formatTemplatePath(page);
                const gradeMeta = STATUS_META[audit?.grade];
                const GradeIcon = gradeMeta?.Icon;
                const pageCount = page.page_count || 1;
                const issueLabels = (audit?.checks || [])
                  .filter((c) => c.status === "fail" || c.status === "warn")
                  .sort((a, b) => (a.status === "fail" ? 0 : 1) - (b.status === "fail" ? 0 : 1))
                  .slice(0, 3)
                  .map((c) => c.label || c.code)
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <li key={page.hash}>
                    <button
                      type="button"
                      onClick={() => onOpenPage(page)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${ROW_HOVER}`}
                    >
                      {GradeIcon ? (
                        <GradeIcon className={`h-6 w-6 shrink-0 ${gradeMeta.text}`} />
                      ) : (
                        <span className="inline-block h-6 w-6 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{displayPath}</p>
                        {issueLabels && (
                          <p className="truncate text-xs text-theme-secondary-text">{issueLabels}</p>
                        )}
                      </div>
                      {audit && <StatusCounts pages={pageCount} counts={counts} />}
                      <RowChevron />
                    </button>
                  </li>
                );
              })}
              {!visible.length && (
                <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                  No pages match this filter.
                </li>
              )}
            </ul>
          )}
        </>
      )}
    />
  );
};
